import React, { useContext } from 'react';
import { Box, Button, Typography, styled, Rating } from '@mui/material';
import ImageComponent from 'ui/components/shared/ImageComponent';
import { LibraryCourseInterface } from 'entities/interfaces';
import { SelectedSkillContext } from 'data/index';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import StarIcon from '@mui/icons-material/Star';

const CardWrapper = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: '0.5rem', // Eckiger
  textTransform: 'none',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  padding: 0,
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.grey[100]}`, // Subtiler Border
  transition: 'all 0.2s ease-in-out',
  width: '100%',
  height: 'auto',
  minHeight: '200px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.grey[200], // Kein gelber Hover
  },
  '&:active': { 
    transform: 'translateY(0px) scale(0.98)'
  },
}));

const ImageBox = styled(Box)({
  width: '100%',
  height: '7rem',
  borderRadius: '0.5rem 0.5rem 0 0', // Angepasst an Card-Radius
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  position: 'relative'
});

const InfoBox = styled(Box)({
  width: '100%',
  padding: '0.75rem 1rem 0.875rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
  flex: 1
});

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.3,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  textOverflow: 'ellipsis',
  minHeight: '2.6rem',
  marginBottom: '0.1rem',
  textAlign: 'left' // LINKSBÜNDIG
}));

const StatsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  marginTop: 'auto'
});

const StatGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem'
});

const Stat = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.875rem',
  fontWeight: 600, // Dicker
  color: '#000' // Schwarz
}));

const RatingsBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%' // Volle Breite aber nicht mehr space-between
});

const RatingValue = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: '#000' // Schwarz
}));

const RatingsCount = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#000', // Schwarz
  fontSize: '0.85rem'
}));

const PointsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#000',
  backgroundColor: theme.palette.primary.light + '20',
  padding: '0.2rem 0.4rem',
  borderRadius: '0.375rem'
}));

const MobileSkillCard = ({ data }: { data: LibraryCourseInterface }) => {
  const { updateContext: setSelectedSkill } = useContext(SelectedSkillContext);

  return (
    <CardWrapper onClick={() => setSelectedSkill(data)}>
      <ImageBox>
        <ImageComponent
          src={data.image}
          alt={data.title}
          width="100%"
          height="100%"
          borderRadius="0.5rem 0.5rem 0 0"
          objectFit
        />
      </ImageBox>
      
      <InfoBox>
        <Title>{data.title}</Title>
        
        <RatingsBox>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Rating
              name={`rating-${data.id}`}
              value={data.ratings ?? 0}
              precision={0.1}
              size="small"
              readOnly
              sx={{ 
                fontSize: '0.9rem',
                '& .MuiRating-iconFilled': {
                  color: '#ffd700'
                },
                '& .MuiRating-iconEmpty': {
                  color: '#ddd'
                }
              }}
            />
            <RatingValue>
              {data.ratings ? data.ratings.toFixed(1) : "–"}
            </RatingValue>
            <RatingsCount>
              ({data.ratingsCount ? data.ratingsCount.toLocaleString().replace(',', '.') : "0"})
            </RatingsCount>
          </Box>
        </RatingsBox>
        
        <StatsRow>
          {typeof data.duration === 'number' && data.duration > 0 && (
            <StatGroup>
              <Stat>
                <HourglassBottomIcon sx={{ fontSize: '1.1rem', color: '#666' }} />
                {data.duration} min
              </Stat>
            </StatGroup>
          )}
          
          {data.coursePoints !== undefined && data.coursePoints > 0 && (
            <PointsBox>
              <StarIcon sx={{ fontSize: '1rem' }} />
              {data.coursePoints}
            </PointsBox>
          )}
        </StatsRow>
      </InfoBox>
    </CardWrapper>
  );
};

export default MobileSkillCard;